import React from "react";
import { Table } from "antd";
import { getDocumentStatus } from "../status";
import { CATEGORY_LABEL, labelFor } from "../labels";

// Matches the badge treatment used on CompletedUsers/GuestDetails (dot + tinted pill)
// rather than a bare antd Tag, so this page reads as part of the same dashboard.
const STATUS_STYLE = {
  red: { bg: "#fdeaec", text: "#b02a37", dot: "#dc3545" },
  amber: { bg: "#fdf2e0", text: "#8a5a00", dot: "#f5a623" },
  green: { bg: "#eaf6ea", text: "#2f7a2e", dot: "#49a847" },
  grey: { bg: "#f1f5f9", text: "#64748b", dot: "#94a3b8" },
};

const Badge = ({ tone, children }) => {
  const c = STATUS_STYLE[tone] || STATUS_STYLE.grey;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 10px",
        borderRadius: 10,
        backgroundColor: c.bg,
        color: c.text,
        fontSize: 12,
        fontWeight: 500,
        lineHeight: "20px",
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: c.dot }}
      />
      {children}
    </span>
  );
};

// Colour is derived at render time from the renewal date, never stored, so it cannot
// go stale in the way a persisted status field would.
const statusBadge = (renewalDate) => {
  const { status, label } = getDocumentStatus(renewalDate && renewalDate.value);
  return <Badge tone={status}>{label}</Badge>;
};

// Whether a field was read off the document or corrected by the customer. Support needs
// this to know if they are looking at our parse or at what the customer says is true.
const sourceBadge = (field) => {
  if (!field || !field.source) return <Badge tone="grey">Not found</Badge>;
  return field.source === "manual" ? (
    <Badge tone="amber">Edited</Badge>
  ) : (
    <Badge tone="grey">Auto</Badge>
  );
};

const fieldValue = (field) =>
  field && field.value !== null && field.value !== undefined ? field.value : "—";

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-GB") : "—";

const DocumentTable = ({ documents, loading, onOpen }) => {
  const columns = [
    {
      title: "Provider",
      dataIndex: "provider",
      key: "provider",
      render: (provider) => (
        <span style={{ fontWeight: 500, color: "#0f172a" }}>
          {fieldValue(provider)}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "documentType",
      key: "documentType",
      render: (type) => (
        <span style={{ color: "#64748b" }}>{labelFor(CATEGORY_LABEL, type)}</span>
      ),
    },
    {
      title: <span style={{ whiteSpace: "nowrap" }}>Annual cost</span>,
      dataIndex: "annualCost",
      key: "annualCost",
      align: "right",
      render: (cost) =>
        cost && cost.value !== null && cost.value !== undefined ? (
          // Tabular figures so the column's decimal points line up.
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            £{Number(cost.value).toFixed(2)}
          </span>
        ) : (
          "—"
        ),
    },
    {
      title: <span style={{ whiteSpace: "nowrap" }}>Renewal date</span>,
      dataIndex: "renewalDate",
      key: "renewalDate",
      render: (date) => (
        <span style={{ whiteSpace: "nowrap" }}>{formatDate(date && date.value)}</span>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => statusBadge(record.renewalDate),
    },
    {
      title: "Fields",
      key: "source",
      render: (_, record) => sourceBadge(record.provider),
    },
    {
      title: "Uploaded",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) => (
        <span style={{ color: "#64748b", whiteSpace: "nowrap" }}>
          {formatDate(value)}
        </span>
      ),
    },
  ];

  return (
    <Table
      rowKey="_id"
      loading={loading}
      dataSource={documents || []}
      columns={columns}
      onRow={(record) => ({
        onClick: () => onOpen(record),
        style: { cursor: "pointer" },
      })}
      pagination={{ pageSize: 20, hideOnSinglePage: true }}
      locale={{ emptyText: "This customer has not uploaded any documents." }}
    />
  );
};

export default DocumentTable;
